import { Button } from '@/components/ui/button';
import Icon from '@/icons/icon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DateRangeCalendarProps {
    startDate: Date | null;
    endDate: Date | null;
    onChange: (start: Date | null, end: Date | null) => void;
    singleMonth?: boolean;
    scrollableYear?: boolean;
    hideQuickSelect?: boolean;
    initialScrollDate?: Date | null;
    allowPastDates?: boolean;
    presetType?: 'future' | 'past';
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

/**
 * Build a midday Date for a calendar day.
 * Using midday (not midnight) avoids DST boundary crossings that would shift
 * a "midnight" Date into the previous day in some timezones.
 */
const calDay = (y: number, m: number, d: number): Date =>
    new Date(y, m, d, 12, 0, 0, 0);

/** Today at midday in the browser's local timezone. */
const getToday = (): Date => {
    const now = new Date();
    return calDay(now.getFullYear(), now.getMonth(), now.getDate());
};

const isSameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

// ─── Quick-select presets ─────────────────────────────────────────────────────

interface Preset {
    label: string;
    start: Date;
    end: Date;
}

const buildPresets = (
    today: Date,
    presetType: 'future' | 'past' = 'future',
): Preset[] => {
    if (presetType === 'past') {
        const last7 = calDay(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - 6,
        );
        const last30 = calDay(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - 29,
        );
        const thisMonthStart = calDay(today.getFullYear(), today.getMonth(), 1);
        const lastMonthStart = calDay(
            today.getFullYear(),
            today.getMonth() - 1,
            1,
        );
        const lastMonthEnd = calDay(today.getFullYear(), today.getMonth(), 0);

        return [
            { label: 'Last 7 days', start: last7, end: today },
            { label: 'Last 30 days', start: last30, end: today },
            { label: 'This month', start: thisMonthStart, end: today },
            { label: 'Last month', start: lastMonthStart, end: lastMonthEnd },
        ];
    }

    const dayOfWeek = today.getDay(); // 0 = Sun

    const daysUntil = (target: number) => (target - dayOfWeek + 7) % 7 || 7;

    const thisSat = calDay(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + daysUntil(6),
    );
    const thisSun = calDay(
        thisSat.getFullYear(),
        thisSat.getMonth(),
        thisSat.getDate() + 1,
    );
    const nextSat = calDay(
        thisSat.getFullYear(),
        thisSat.getMonth(),
        thisSat.getDate() + 7,
    );
    const nextSun = calDay(
        nextSat.getFullYear(),
        nextSat.getMonth(),
        nextSat.getDate() + 1,
    );
    const tomorrow = calDay(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + 1,
    );
    const dayAfter = calDay(
        tomorrow.getFullYear(),
        tomorrow.getMonth(),
        tomorrow.getDate() + 1,
    );

    return [
        { label: 'Tonight', start: today, end: tomorrow },
        { label: 'Tomorrow', start: tomorrow, end: dayAfter },
        { label: 'Weekend', start: thisSat, end: thisSun },
        { label: 'Next weekend', start: nextSat, end: nextSun },
    ];
};

// ─── Single month grid ────────────────────────────────────────────────────────

interface MonthGridProps {
    month: Date;
    today: Date;
    startDate: Date | null;
    endDate: Date | null;
    onDayClick: (day: Date) => void;
    showPrev?: boolean;
    showNext?: boolean;
    onPrev?: () => void;
    onNext?: () => void;
    compact?: boolean;
    allowPastDates?: boolean;
}

function MonthGrid({
    month,
    today,
    startDate,
    endDate,
    onDayClick,
    showPrev,
    showNext,
    onPrev,
    onNext,
    compact = false,
    allowPastDates = false,
}: MonthGridProps) {
    const year = month.getFullYear();
    const mo = month.getMonth();
    const daysInMonth = new Date(year, mo + 1, 0).getDate();
    // Monday-first: Sun=0 → offset 6, Mon=1 → offset 0
    const startOffset = (new Date(year, mo, 1).getDay() + 6) % 7;
    const monthLabel = month.toLocaleString('default', { month: 'long' });
    const cellBase = compact ? 'mx-auto h-9 w-9 text-sm' : 'h-8 w-8';

    return (
        <div
            className={
                compact ? 'w-full px-4 py-1' : 'w-72 rounded-lg bg-card p-3'
            }
        >
            {/* Header */}
            <div
                className={`mb-3 flex items-center ${compact ? 'justify-center' : 'justify-between'}`}
            >
                {showPrev ? (
                    <Button
                        variant="bare"
                        type="button"
                        onClick={onPrev}
                        aria-label="Previous month"
                        className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
                    >
                        <Icon
                            iconName="ChevronLeft"
                            className="h-5 w-5 stroke-gray-700"
                        />
                    </Button>
                ) : (
                    <div className="w-8" />
                )}

                <span
                    className={`font-semibold ${compact ? 'text-base' : 'text-lg'}`}
                >
                    {monthLabel} {year}
                </span>

                {showNext ? (
                    <Button
                        variant="bare"
                        type="button"
                        onClick={onNext}
                        aria-label="Next month"
                        className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
                    >
                        <Icon
                            iconName="ChevronRight"
                            className="h-5 w-5 stroke-gray-700"
                        />
                    </Button>
                ) : (
                    <div className="w-8" />
                )}
            </div>

            {/* Weekday labels */}
            <div
                className={`mb-1 grid grid-cols-7 text-center text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}
            >
                {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
                    <div key={d}>{d}</div>
                ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-y-0.5 text-center">
                {Array.from({ length: startOffset }, (_, i) => (
                    <div key={`sp-${i}`} className={cellBase} />
                ))}

                {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = calDay(year, mo, i + 1);
                    const past =
                        !allowPastDates &&
                        day < today &&
                        !isSameDay(day, today);
                    const isStart = startDate
                        ? isSameDay(day, startDate)
                        : false;
                    const isEnd = endDate ? isSameDay(day, endDate) : false;
                    const isMid = !!(
                        startDate &&
                        endDate &&
                        day > startDate &&
                        day < endDate
                    );

                    return (
                        <Button
                            variant="bare"
                            key={i}
                            type="button"
                            disabled={past}
                            onClick={() => !past && onDayClick(day)}
                            aria-label={`${year}-${String(mo + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`}
                            className={[
                                'flex items-center justify-center rounded-full transition-colors',
                                cellBase,
                                past
                                    ? 'cursor-not-allowed opacity-25'
                                    : 'cursor-pointer hover:bg-blue-100',
                                isStart || isEnd
                                    ? 'bg-blue-600 font-semibold text-white hover:bg-blue-700'
                                    : '',
                                isMid && !isStart && !isEnd
                                    ? 'rounded-full bg-blue-100'
                                    : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            {i + 1}
                        </Button>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DateRangeCalendar({
    startDate,
    endDate,
    onChange,
    singleMonth = false,
    scrollableYear = false,
    hideQuickSelect = false,
    initialScrollDate = null,
    allowPastDates = false,
    presetType = 'future',
}: DateRangeCalendarProps) {
    const today = useMemo(() => getToday(), []);

    const [viewMonth, setViewMonth] = useState<Date>(() => startDate ?? today);

    const nextViewMonth = useMemo(
        () => new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1),
        [viewMonth],
    );

    const goPrev = useCallback(
        () =>
            setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1)),
        [],
    );
    const goNext = useCallback(
        () =>
            setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1)),
        [],
    );

    const handleDayClick = useCallback(
        (day: Date) => {
            if (!allowPastDates && day < today && !isSameDay(day, today))
                return;

            if (!startDate) {
                onChange(day, null);
                return;
            }
            if (!endDate) {
                if (isSameDay(day, startDate) || day < startDate) {
                    onChange(day, null);
                } else {
                    onChange(startDate, day);
                }
                return;
            }
            // Both set → restart selection
            onChange(day, null);
        },
        [startDate, endDate, today, onChange, allowPastDates],
    );

    const scrollMonths = useMemo(
        () =>
            scrollableYear
                ? Array.from(
                      { length: 13 },
                      (_, i) =>
                          new Date(
                              today.getFullYear(),
                              today.getMonth() + i,
                              1,
                          ),
                  )
                : [],
        [scrollableYear, today],
    );

    const scrollListRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!scrollableYear || !initialScrollDate) return;
        const target = scrollListRef.current?.querySelector(
            `[data-cal-month="${initialScrollDate.getFullYear()}-${initialScrollDate.getMonth()}"]`,
        );
        if (target) {
            target.scrollIntoView({ block: 'start' });
        }
        // Run once on mount only — intentionally omitting deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const presets = useMemo(
        () => buildPresets(today, presetType),
        [today, presetType],
    );

    return (
        <div
            className={
                scrollableYear
                    ? 'w-full text-black dark:text-foreground'
                    : 'mx-auto w-full max-w-md rounded-2xl bg-card p-4 text-black shadow-xl md:max-w-2xl dark:text-foreground'
            }
        >
            {scrollableYear ? (
                <div ref={scrollListRef} className="flex flex-col">
                    {scrollMonths.map((m) => (
                        <div
                            key={`${m.getFullYear()}-${m.getMonth()}`}
                            data-cal-month={`${m.getFullYear()}-${m.getMonth()}`}
                        >
                            <MonthGrid
                                month={m}
                                today={today}
                                startDate={startDate}
                                endDate={endDate}
                                onDayClick={handleDayClick}
                                compact
                                allowPastDates={allowPastDates}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div
                    className={`flex gap-4 overflow-x-auto ${
                        singleMonth
                            ? 'justify-center'
                            : 'flex-col md:flex-row md:overflow-visible'
                    }`}
                >
                    <MonthGrid
                        month={viewMonth}
                        today={today}
                        startDate={startDate}
                        endDate={endDate}
                        onDayClick={handleDayClick}
                        showPrev
                        showNext={singleMonth}
                        onPrev={goPrev}
                        onNext={goNext}
                        allowPastDates={allowPastDates}
                    />
                    {!singleMonth && (
                        <MonthGrid
                            month={nextViewMonth}
                            today={today}
                            startDate={startDate}
                            endDate={endDate}
                            onDayClick={handleDayClick}
                            showNext
                            onNext={goNext}
                            allowPastDates={allowPastDates}
                        />
                    )}
                </div>
            )}

            {!hideQuickSelect && (
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
                    {presets.map(({ label, start, end }) => (
                        <Button
                            variant="bare"
                            key={label}
                            type="button"
                            onClick={() => onChange(start, end)}
                            className="cursor-pointer rounded-full border border-gray-200 p-2 text-sm font-semibold whitespace-nowrap text-gray-900 hover:bg-blue-100"
                        >
                            {label}
                        </Button>
                    ))}
                    <Button
                        variant="bare"
                        type="button"
                        onClick={() => onChange(null, null)}
                        className="cursor-pointer rounded-full border border-gray-200 p-2 text-sm font-semibold whitespace-nowrap text-gray-900 hover:bg-blue-100"
                    >
                        Clear
                    </Button>
                </div>
            )}
        </div>
    );
}
